import { useState, useEffect, useCallback, useMemo } from "react";
import {
  useResources,
  resource,
  withKey,
  type ResourceElement,
} from "@assistant-ui/tap";
import type { ClientOutput } from "@assistant-ui/store";
import {
  attachTransformScopes,
  useAssistantClientRef,
  useAssistantScopeEffect,
} from "@assistant-ui/store/client";
import type { McpAppResourceOutput, ToolsState } from "../types/scopes/tools";
import type { Tool } from "assistant-stream";
import {
  isStandaloneToolDisplay,
  makeToolCallTextComponent,
  type ToolCallText,
  type Toolkit,
} from "../model-context/toolbox";
import type { ToolCallMessagePartComponent } from "../types/MessagePartComponentTypes";
import { ModelContext } from "../../store/clients/model-context-client";
import { nullProtoRecord } from "../../utils/record";
import { runCleanups } from "../../subscribable/subscribable";

export type { McpAppResourceOutput };

/**
 * Registers tools with model context and installs tool-call renderers.
 *
 * Mount this resource near an assistant subtree when you want to expose a
 * group of tools declaratively. Tool definitions are registered with model
 * context, while each tool renderer is registered with the tools scope for
 * message rendering.
 */
const useTools = ({
  toolkit,
  mcpApp,
}: {
  /** Tools to expose to the model and optional renderers to install. */
  toolkit?: Toolkit;
  /** Optional MCP app resource whose tools should be merged into context. */
  mcpApp?: ResourceElement<McpAppResourceOutput> | undefined;
}): ClientOutput<"tools"> => {
  const mcpAppOutputs = useResources(mcpApp ? [withKey("mcpApp", mcpApp)] : []);
  const mcpAppOutput = mcpAppOutputs[0];

  const [toolUIs, setToolUIs] = useState<ToolsState["toolUIs"]>(() =>
    nullProtoRecord(),
  );

  const state = useMemo(
    (): ToolsState => ({
      toolUIs,
      mcpApp: mcpAppOutput,
    }),
    [toolUIs, mcpAppOutput],
  );

  const clientRef = useAssistantClientRef();

  const setToolUI = useCallback(
    (
      toolName: string,
      render: ToolCallMessagePartComponent,
      options?: {
        standalone?: boolean;
        renderText?: ToolCallText<any, any> | undefined;
      },
    ) => {
      // One registration object per call; identity is the removal key, so
      // the per-name list stays correctly ref-counted across re-registers.
      const registration = {
        render,
        renderText: options?.renderText,
        standalone: options?.standalone ?? false,
      };

      setToolUIs((prev) => {
        const next = nullProtoRecord(prev);
        next[toolName] = [...(next[toolName] ?? []), registration];
        return next;
      });

      return () => {
        setToolUIs((prev) => {
          const registrations =
            prev[toolName]?.filter((r) => r !== registration) ?? [];
          const next = nullProtoRecord(prev);
          if (registrations.length > 0) {
            next[toolName] = registrations;
            return next;
          }
          // Drop the key entirely so repeatedly mounted/unmounted tools
          // don't leave empty arrays accumulating across a long session.
          delete next[toolName];
          return next;
        });
      };
    },
    [],
  );

  useEffect(() => {
    if (!toolkit) return;
    const unsubscribes: (() => void)[] = [];

    // Register tool UIs (exclude symbols)
    for (const [toolName, tool] of Object.entries(toolkit)) {
      const toolRender = "render" in tool ? tool.render : undefined;
      const toolRenderText = "renderText" in tool ? tool.renderText : undefined;
      const render =
        toolRender ??
        (toolRenderText
          ? makeToolCallTextComponent(toolRenderText)
          : undefined);
      if (render) {
        unsubscribes.push(
          // Registration has to be undone on unmount, so the registry write and
          // its unsubscribe belong to the same effect.
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setToolUI(toolName, render, {
            standalone: isStandaloneToolDisplay(tool),
            renderText: toolRenderText,
          }),
        );
      }
    }

    return () => runCleanups(unsubscribes);
  }, [toolkit, setToolUI]);

  useAssistantScopeEffect(
    "modelContext",
    () => {
      if (!toolkit) return;

      // Register tools with model context (exclude symbols). `render`,
      // `renderText`, and `display` are client-only presentation concerns and
      // never reach the model.
      const toolsWithoutRender = Object.entries(toolkit).reduce(
        (acc, [name, tool]) => {
          if (tool.type === "mcp") return acc;
          const {
            display: _display,
            render: _render,
            renderText: _renderText,
            ...rest
          } = tool as typeof tool & { renderText?: unknown };
          acc[name] = rest as Tool<any, any>;
          return acc;
        },
        nullProtoRecord<Tool<any, any>>(),
      );

      const modelContextProvider = {
        getModelContext: () => ({
          tools: toolsWithoutRender,
        }),
      };

      return clientRef.current!.modelContext().register(modelContextProvider);
    },
    [toolkit],
  );

  return {
    getState: () => state,
    setToolUI,
  };
};

export const Tools = resource(useTools);

attachTransformScopes(useTools, (scopes, parent) => {
  if (!scopes.modelContext && parent.modelContext.source === null) {
    scopes.modelContext = ModelContext();
  }
});
